import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Drawer, Button, Input, Space, Row, Col, TreeSelect, InputNumber, DatePicker, Modal, Collapse, Radio, Empty, Checkbox, Spin, Form } from 'antd';
import { DeleteOutlined, FormOutlined, SaveOutlined, UserOutlined } from '@ant-design/icons';
import { isMobile } from 'react-device-detect';
import moment from 'moment';
import { createService, getSpecificService, updateService } from '@/redux/actions/customerServicesActions';
import { fetchServiceDetail } from '@/redux/actions/serviceActions';
import { fetchCustomer } from '@/redux/actions/customerActions';
import { makeTree, _newLine, customerServiceStatus } from '@/utils/helpers';
import BaseSelect from '@/components/Elements/BaseSelect';
import CustomerServiceIndependentStaffFields from './CustomerServiceIndependentStaffFields';
import { styles } from './styles';
import { applyUnknownStaffFieldChange, cloneCustomerServiceFormData } from './customerServiceFormStateHelpers';


const { Panel } = Collapse;
const { confirm } = Modal;
const uniqueIds = (values = []) => Array.from(new Set(values.map(Number).filter(Boolean)));
const DEFAULT_FINANCIAL_CHANGE_REASON = 'Hệ thống tự ghi nhận khi người dùng thay đổi giá trị dịch vụ.';
const DEFAULT_SELECTED_AT_CHANGE_REASON = 'Hệ thống tự ghi nhận khi người dùng thay đổi ngày chốt.';
const createSelectedAtRequestKey = serviceId => (
    `CUSTOMER-SERVICE-SELECTED-AT-${serviceId}-${Date.now()}-${Math.random().toString(36).slice(2)}`
);

function CustomerServiceForm({
    visible,
    customerId,
    currentData,
    customerServiceId,
    reloadData,
    onCancel,
}) {
    const dispatch = useDispatch();
    const [form] = Form.useForm();

    const currentCustomer = useSelector((s) => s.customerReducer?.detail);

    const doctors = useSelector((s) => s.config.doctors);
    const assistants = useSelector((s) => s.config.assistants);
    const sales = useSelector((s) => s.config.sales);
    const implantServiceGroupId = Number(useSelector((s) => s.config.implant_service_group_id)) || null;
    const filterData = useSelector((s) => s.filterReducer.list);

    const services = filterData?.services || [];
    const serviceTree = useMemo(() => makeTree(services || [], 0), [services]);

    const [isLoading, setIsLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [editData, setEditData] = useState(null);
    const loadRequestRef = useRef(0);
    const selectedAtRequestKeyRef = useRef(null);

    const [items, setItems] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [selectAll, setSelectAll] = useState(false);

    const [activeCollapseKeys, setActiveCollapseKeys] = useState([]);
    const [selectedAtValue, setSelectedAtValue] = useState(null);

    const [bulkVisible, setBulkVisible] = useState(false);
    const [bulkData, setBulkData] = useState({
        sales: [],
        assistants: [],
        examination_doctor_ids: [],
        treatment_doctor_ids: [],
        implant_doctor_ids: [],
        prosthetic_doctor_ids: [],
        apprentice_doctor_ids: [],
        mode: 'update',
    });

    const editServiceId = Number(customerServiceId || currentData?.id) || null;
    const isEditMode = !!editServiceId;
    const boundCustomerId = isEditMode
        ? Number(editData?.customer_id || editData?.customer?.id) || null
        : Number(customerId) || null;
    const isImplantService = useCallback((item) => (
        implantServiceGroupId !== null && Number(item?.service_group_id) === implantServiceGroupId
    ), [implantServiceGroupId]);

    useEffect(() => {
        if (!visible || !boundCustomerId) return;
        dispatch(fetchCustomer(boundCustomerId));
    }, [visible, boundCustomerId, dispatch]);

    useEffect(() => {
        const requestId = loadRequestRef.current + 1;
        loadRequestRef.current = requestId;

        if (!visible || !editServiceId) {
            setEditData(null);
            return undefined;
        }

        let active = true;
        setEditData(null);
        setIsLoading(true);

        dispatch(getSpecificService(editServiceId))
            .then((data) => {
                if (active && loadRequestRef.current === requestId) {
                    setEditData(cloneCustomerServiceFormData(data));
                }
            })
            .catch(() => {
                // Redux Action đã hiển thị lỗi theo response backend.
            })
            .finally(() => {
                if (active && loadRequestRef.current === requestId) {
                    setIsLoading(false);
                }
            });

        return () => {
            active = false;
        };
    }, [visible, editServiceId, dispatch]);

    const calculateAmount = useCallback((item) => {
        const q = Number(item.quantity) || 0;
        const p = Number(item.sale_price) || 0;
        return q * p;
    }, []);

    const extractSalesIds = useCallback((list = []) => {
        return list
            .map((x) => Number(x?.sales_id ?? x?.sales?.id ?? x?.user_id ?? x?.id ?? x))
            .filter(Boolean);
    }, []);

    const extractDoctorIds = useCallback((list = []) => {
        return list
            .map((x) => Number(x?.doctor_id ?? x?.doctor?.id ?? x?.user_id ?? x?.id ?? x))
            .filter(Boolean);
    }, []);

    const extractAssistantIds = useCallback((list = []) => {
        return list
            .map((x) => Number(x?.assistant_id ?? x?.assistant?.id ?? x?.user_id ?? x?.id ?? x))
            .filter(Boolean);
    }, []);

    const extractRoleIds = useCallback((list = []) => uniqueIds((list || []).map(
        (x) => x?.employee_id ?? x?.doctor_id ?? x?.doctor?.id ?? x?.user_id ?? x?.id ?? x
    )), []);

    const normalizeItems = useCallback((rawItems = []) => {
        return rawItems.map((it, index) => {
            const quantity = Number(it.quantity) || 1;
            const salePrice = Number(it.sale_price ?? it.regular_price ?? it.price) || 0;
            const amount = Number(it.amount);
            const serviceGroup = it?.service_group_info || (
                typeof it?.service_group === 'object' ? it.service_group : null
            ) || it?.service?.service_group || null;
            const itemIsImplant = serviceGroup?.code === 'implant'
                || (implantServiceGroupId !== null
                    && Number(serviceGroup?.id || it.service_group_id || it?.service?.service_group_id) === implantServiceGroupId);
            const salesIds = extractSalesIds(it.sales || []);
            const assistantIds = extractAssistantIds(it.assistants || []);
            const legacyDoctorIds = extractDoctorIds(it.doctors || []);
            const treatmentDoctorIds = Array.isArray(it.treatment_doctor_ids)
                ? uniqueIds(it.treatment_doctor_ids)
                : (extractRoleIds(it.treatment_doctors || []).length
                    ? extractRoleIds(it.treatment_doctors || []) : legacyDoctorIds);
            const implantDoctorIds = Array.isArray(it.implant_doctor_ids)
                ? uniqueIds(it.implant_doctor_ids)
                : (extractRoleIds(it.implant_doctors || []).length
                    ? extractRoleIds(it.implant_doctors || [])
                    : uniqueIds((it.doctors || []).filter(x => Number(x?.is_implant_doctor) === 1).map(x => x?.doctor_id ?? x?.employee_id)));
            const prostheticDoctorIds = Array.isArray(it.prosthetic_doctor_ids)
                ? uniqueIds(it.prosthetic_doctor_ids)
                : (extractRoleIds(it.prosthetic_doctors || []).length
                    ? extractRoleIds(it.prosthetic_doctors || [])
                    : uniqueIds((it.doctors || []).filter(x => Number(x?.is_prosthetic) === 1).map(x => x?.doctor_id ?? x?.employee_id)));

            return {
                id: it.id || `tmp_${index}_${Date.now()}`,
                service_id: it.service_id || it.id,
                service_group: serviceGroup?.code || it.service_group || null,
                service_group_id: serviceGroup?.id || it.service_group_id || it?.service?.service_group_id || null,
                service_group_name: serviceGroup?.name || it.service_group_name || '',
                service_group_info: serviceGroup || it.service_group_info || null,
                name: it.name || it.service_name || it?.service?.name || '',
                regular_price: Number(it.regular_price ?? it.price) || 0,
                sale_price: salePrice,
                quantity,
                amount: Number.isFinite(amount) ? amount : quantity * salePrice,
                status: it.status ?? null,
                note: _newLine(it.note || ''),
                sales: salesIds,
                sales_unknown: typeof it.sales_unknown === 'boolean' ? it.sales_unknown : salesIds.length === 0,
                assistants: assistantIds,
                examination_doctor_ids: Array.isArray(it.examination_doctor_ids)
                    ? uniqueIds(it.examination_doctor_ids)
                    : extractRoleIds(it.examination_doctors || []),
                // Dữ liệu legacy full_service được đưa vào Bác sĩ điều trị khi mở form lần đầu.
                treatment_doctor_ids: treatmentDoctorIds,
                implant_doctor_ids: implantDoctorIds,
                prosthetic_doctor_ids: prostheticDoctorIds,
                direct_doctor_unknown: typeof it.direct_doctor_unknown === 'boolean'
                    ? it.direct_doctor_unknown
                    : (itemIsImplant
                        ? [...treatmentDoctorIds, ...implantDoctorIds, ...prostheticDoctorIds]
                        : treatmentDoctorIds).length === 0,
                apprentice_doctor_ids: Array.isArray(it.apprentice_doctor_ids)
                    ? uniqueIds(it.apprentice_doctor_ids)
                    : (extractRoleIds(it.apprentice_doctors || []).length
                        ? extractRoleIds(it.apprentice_doctors || [])
                        : uniqueIds((it.doctors || []).filter(x => Number(x?.is_apprentice) === 1).map(x => x?.doctor_id ?? x?.employee_id))),
            };
        });
    }, [
        extractSalesIds,
        extractDoctorIds,
        extractAssistantIds,
        extractRoleIds,
        implantServiceGroupId,
    ]);

    const recalcTotalAmount = useCallback((nextItems) => {
        const total = (nextItems || []).reduce((sum, it) => sum + (Number(it.amount) || 0), 0);
        form.setFieldsValue({ total_amount: total });
    }, [form]);

    const financialChanged = useMemo(() => {
        if (!isEditMode || !items.length || !editData) return false;
        const item = items[0];
        return Number(item.quantity) !== Number(editData.quantity)
            || Number(item.sale_price) !== Number(editData.sale_price)
            || Number(item.amount) !== Number(editData.amount);
    }, [isEditMode, items, editData]);

    const selectedAtChanged = useMemo(() => {
        if (!isEditMode || !editData || !selectedAtValue) return false;
        // Bản ghi legacy có thể chưa có selected_at dù UI đang hiển thị created_at làm giá trị thay thế.
        // Khi lưu, backend vẫn xem đây là lần khởi tạo ngày chốt và cần đủ reason/idempotency key.
        return !editData.selected_at || !moment(editData.selected_at).isSame(selectedAtValue, 'day');
    }, [isEditMode, editData, selectedAtValue]);

    const resetLocalState = useCallback(() => {
        setSaveLoading(false);
        setItems([]);
        setSelectedItems([]);
        setSelectAll(false);
        setBulkVisible(false);
        setActiveCollapseKeys([]);
        setSelectedAtValue(null);
        selectedAtRequestKeyRef.current = null;
        setBulkData({ sales: [], assistants: [], examination_doctor_ids: [], treatment_doctor_ids: [], implant_doctor_ids: [], prosthetic_doctor_ids: [], apprentice_doctor_ids: [], mode: 'update' });
    }, []);

    const onClose = useCallback(() => {
        loadRequestRef.current += 1;
        form.resetFields();
        resetLocalState();
        setEditData(null);
        setIsLoading(false);
        onCancel();
    }, [onCancel, form, resetLocalState]);

    useEffect(() => {
        if (!visible) return;

        if (isEditMode) {
            if (!editData || Number(editData.id) !== editServiceId) {
                form.resetFields();
                resetLocalState();
                return;
            }
            const rawItems = editData?.items?.length
                ? editData.items.map((item) => ({
                    ...item,
                    sales: item.sales ?? editData?.sales ?? [],
                    doctors: item.doctors ?? editData?.doctors ?? [],
                    assistants: item.assistants ?? editData?.assistants ?? [],
                    service_id: item.service_id ?? editData?.service_id ?? editData?.service?.id,
                    service_group: item.service_group ?? item?.service?.service_group ?? editData?.service_group ?? editData?.service?.service_group,
                    service_group_info: item.service_group_info ?? item?.service?.service_group ?? editData?.service?.service_group,
                    name: item.name ?? editData?.service_name ?? editData?.service?.name,
                    price: item.price ?? editData?.service?.price,
                }))
                : [{
                    ...editData,
                    sales: editData?.sales ?? [],
                    doctors: editData?.doctors ?? [],
                    assistants: editData?.assistants ?? [],
                    service_id: editData?.service_id ?? editData?.service?.id,
                    service_group: editData?.service_group ?? editData?.service?.service_group,
                    service_group_info: editData?.service_group_info ?? editData?.service?.service_group,
                    name: editData?.service_name ?? editData?.service?.name,
                    price: editData?.price ?? editData?.service?.price,
                }];

            const normalizedItems = normalizeItems(rawItems);
            const openedKeys = normalizedItems.map((item) => String(item.id));
            const selectedAtRequestKey = createSelectedAtRequestKey(editData.id);
            selectedAtRequestKeyRef.current = selectedAtRequestKey;

            setItems(normalizedItems);
            setSelectedItems([]);
            setSelectAll(false);
            setActiveCollapseKeys(openedKeys);

            form.setFieldsValue({
                selected_service_id: null,
                selected_at: editData?.selected_at
                    ? moment(editData.selected_at)
                    : editData?.created_at
                        ? moment(editData.created_at)
                        : moment(),
                total_amount: normalizedItems.reduce((sum, it) => sum + (Number(it.amount) || 0), 0),
                financial_change_reason: null,
                financial_change_idempotency_key: `CUSTOMER-SERVICE-${editData?.id}-${Date.now()}`,
                selected_at_change_reason: null,
                selected_at_change_idempotency_key: selectedAtRequestKey,
            });
            setSelectedAtValue(editData?.selected_at ? moment(editData.selected_at) : moment(editData?.created_at || undefined));
        } else {
            form.setFieldsValue({
                selected_at: moment(),
                selected_service_id: null,
                total_amount: 0,
            });
            setSelectedAtValue(moment());
            setItems([]);
            setSelectedItems([]);
            setSelectAll(false);
            setActiveCollapseKeys([]);
        }
    }, [visible, isEditMode, editServiceId, editData, form, normalizeItems, resetLocalState]);

    const onChangeService = useCallback(async (id) => {
        if (!id) return;

        setIsLoading(true);
        try {
            const item = await dispatch(fetchServiceDetail(id));
            if (!item) return;

            const newItem = {
                id: item.id,
                service_id: item.id,
                service_group: item.service_group?.code || null,
                service_group_id: item.service_group_id || item.service_group?.id || null,
                service_group_name: item.service_group?.name || '',
                name: item.name,
                regular_price: Number(item.price) || 0,
                sale_price: Number(item.price) || 0,
                quantity: 1,
                amount: Number(item.price) || 0,
                status: null,
                note: '',
                sales: [],
                sales_unknown: false,
                examination_doctor_ids: [],
                treatment_doctor_ids: [],
                implant_doctor_ids: [],
                prosthetic_doctor_ids: [],
                direct_doctor_unknown: false,
                apprentice_doctor_ids: [],
                assistants: [],
            };

            setItems((prev) => {
                const exists = prev.some((x) => String(x.service_id || x.id) === String(newItem.service_id));
                const next = exists ? prev : [...prev, newItem];

                const normalized = normalizeItems(next);
                setSelectedItems((old) => old.filter((sid) => normalized.some((x) => x.id === sid)));
                setSelectAll(false);
                recalcTotalAmount(normalized);
                setActiveCollapseKeys(normalized.map((x) => String(x.id)));

                return normalized;
            });

            form.setFieldsValue({ selected_service_id: null });
        } finally {
            setIsLoading(false);
        }
    }, [dispatch, normalizeItems, recalcTotalAmount, form]);

    const onChangeItem = useCallback((name, value, itemId) => {
        setItems((prev) => {
            const next = [...prev];
            const idx = next.findIndex((it) => String(it.id) === String(itemId));
            if (idx < 0) return prev;

            const cur = applyUnknownStaffFieldChange(next[idx], name, value);

            if (name === 'quantity' || name === 'sale_price') {
                cur.amount = calculateAmount(cur);
            }

            if (name === 'amount') {
                const q = Number(cur.quantity) || 1;
                cur.sale_price = q ? Number(((Number(cur.amount) || 0) / q).toFixed(6)) : 0;
            }

            if (name.endsWith('_doctor_ids')) {
                cur[name] = uniqueIds(value || []);
            }

            next[idx] = cur;

            const normalized = normalizeItems(next);
            recalcTotalAmount(normalized);
            return normalized;
        });
    }, [calculateAmount, normalizeItems, recalcTotalAmount]);

    const onDelete = useCallback((id) => {
        confirm({
            title: 'Cảnh báo',
            content: 'Xóa dịch vụ này?',
            okText: 'OK',
            cancelText: 'Huỷ',
            onOk: () => {
                setItems((prev) => {
                    const next = prev.filter((it) => String(it.id) !== String(id));

                    setSelectedItems((old) => old.filter((sid) => String(sid) !== String(id)));
                    setSelectAll(false);

                    const normalized = normalizeItems(next);
                    recalcTotalAmount(normalized);
                    return normalized;
                });
            },
        });
    }, [normalizeItems, recalcTotalAmount]);

    const handleSelectAll = useCallback((e) => {
        const checked = e.target.checked;
        setSelectAll(checked);
        setSelectedItems(checked ? items.map((it) => it.id) : []);
    }, [items]);

    const handleItemSelect = useCallback((itemId, checked) => {
        setSelectedItems((prev) => {
            const next = checked ? [...prev, itemId] : prev.filter((id) => id !== itemId);
            setSelectAll(next.length === items.length && items.length > 0);
            return next;
        });
    }, [items.length]);

    const intersection = useCallback((arrays) => {
        const list = (arrays || []).filter(Boolean).map((a) => a || []);
        if (!list.length) return [];
        const first = list[0] || [];
        return first.filter((x) => list.every((arr) => (arr || []).includes(x)));
    }, []);

    const openBulkUpdate = useCallback(() => {
        if (!selectedItems?.length) return;

        const selectedRows = items.filter((it) => selectedItems.includes(it.id));

        setBulkVisible(true);
        setBulkData({
            mode: 'update',
            sales: intersection(selectedRows.map((r) => r.sales || [])),
            assistants: intersection(selectedRows.map((r) => r.assistants || [])),
            examination_doctor_ids: intersection(selectedRows.map((r) => r.examination_doctor_ids || [])),
            treatment_doctor_ids: intersection(selectedRows.map((r) => r.treatment_doctor_ids || [])),
            implant_doctor_ids: intersection(selectedRows.map((r) => r.implant_doctor_ids || [])),
            prosthetic_doctor_ids: intersection(selectedRows.map((r) => r.prosthetic_doctor_ids || [])),
            apprentice_doctor_ids: intersection(selectedRows.map((r) => r.apprentice_doctor_ids || [])),
        });
    }, [items, selectedItems, intersection]);

    const closeBulkUpdate = useCallback(() => setBulkVisible(false), []);
    const setBulkField = useCallback((key, value) => {
        setBulkData((prev) => ({ ...prev, [key]: value || [] }));
    }, []);

    const applyBulkUpdate = useCallback(() => {
        const { sales: s, assistants: a, mode } = bulkData;
        const uniq = (arr) => Array.from(new Set((arr || []).filter(Boolean)));

        const next = items.map((it) => {
            if (!selectedItems.includes(it.id)) return it;

            if (mode === 'clear') {
                return { ...it, sales: [], assistants: [], examination_doctor_ids: [], treatment_doctor_ids: [], implant_doctor_ids: [], prosthetic_doctor_ids: [], apprentice_doctor_ids: [] };
            }

            return {
                ...it,
                sales: s?.length ? uniq(s) : it.sales,
                sales_unknown: s?.length ? false : it.sales_unknown,
                assistants: a?.length ? uniq(a) : it.assistants,
                examination_doctor_ids: bulkData.examination_doctor_ids?.length ? uniq(bulkData.examination_doctor_ids) : it.examination_doctor_ids,
                treatment_doctor_ids: bulkData.treatment_doctor_ids?.length ? uniq(bulkData.treatment_doctor_ids) : it.treatment_doctor_ids,
                implant_doctor_ids: bulkData.implant_doctor_ids?.length ? uniq(bulkData.implant_doctor_ids) : it.implant_doctor_ids,
                prosthetic_doctor_ids: bulkData.prosthetic_doctor_ids?.length ? uniq(bulkData.prosthetic_doctor_ids) : it.prosthetic_doctor_ids,
                direct_doctor_unknown: [
                    ...(bulkData.treatment_doctor_ids || []),
                    ...(bulkData.implant_doctor_ids || []),
                    ...(bulkData.prosthetic_doctor_ids || []),
                ].length ? false : it.direct_doctor_unknown,
                apprentice_doctor_ids: bulkData.apprentice_doctor_ids?.length ? uniq(bulkData.apprentice_doctor_ids) : it.apprentice_doctor_ids,
            };
        });

        const normalized = normalizeItems(next);
        setItems(normalized);
        recalcTotalAmount(normalized);
        setBulkVisible(false);
    }, [bulkData, items, selectedItems, normalizeItems, recalcTotalAmount]);


    const buildPayloadItems = useCallback(() => {
        return (items || []).map((it) => ({
            id: it.id,
            service_id: it.service_id || it.id,
            name: it.name || '',
            quantity: Number(it.quantity) || 1,
            regular_price: Number(it.regular_price) || 0,
            sale_price: Number(it.sale_price) || 0,
            amount: Number(it.amount) || 0,
            status: it.status,
            note: it.note || '',
            sales: it.sales || [],
            sales_unknown: it.sales_unknown === true,
            assistants: it.assistants || [],
            examination_doctor_ids: uniqueIds(it.examination_doctor_ids || []),
            treatment_doctor_ids: uniqueIds(it.treatment_doctor_ids || []),
            implant_doctor_ids: isImplantService(it) ? uniqueIds(it.implant_doctor_ids || []) : [],
            prosthetic_doctor_ids: isImplantService(it) ? uniqueIds(it.prosthetic_doctor_ids || []) : [],
            direct_doctor_unknown: it.direct_doctor_unknown === true,
            apprentice_doctor_ids: uniqueIds(it.apprentice_doctor_ids || []),
        }));
    }, [items, isImplantService]);

    const handleSave = useCallback(async () => {
        try {
            const values = await form.validateFields();

            if (!items.length) {
                return;
            }

            for (const item of items) {
                if (!(item.sales || []).length && item.sales_unknown !== true) {
                    Modal.warning({
                        title: 'Thiếu nhân viên sales',
                        content: `Dịch vụ "${item.name}" chưa chọn nhân viên sales hoặc xác nhận Chưa xác định.`,
                    });
                    return;
                }

                const directDoctorIds = isImplantService(item)
                    ? [...(item.treatment_doctor_ids || []), ...(item.implant_doctor_ids || []), ...(item.prosthetic_doctor_ids || [])]
                    : (item.treatment_doctor_ids || []);
                if (!directDoctorIds.length && item.direct_doctor_unknown !== true) {
                    Modal.warning({
                        title: 'Thiếu bác sĩ phụ trách',
                        content: `Dịch vụ "${item.name}" chưa chọn bác sĩ điều trị phù hợp hoặc xác nhận Chưa xác định.`,
                    });
                    return;
                }
            }

            setSaveLoading(true);

            let selectedAtRequestKey = null;
            if (selectedAtChanged) {
                selectedAtRequestKey = values.selected_at_change_idempotency_key
                    || selectedAtRequestKeyRef.current
                    || createSelectedAtRequestKey(editServiceId);
                selectedAtRequestKeyRef.current = selectedAtRequestKey;
                form.setFieldsValue({ selected_at_change_idempotency_key: selectedAtRequestKey });
            }

            const payload = {
                customer_id: boundCustomerId,
                selected_at: values.selected_at ? values.selected_at.format('YYYY-MM-DD') : null,
                total_amount: Number(values.total_amount) || 0,
                financial_change_reason: financialChanged ? DEFAULT_FINANCIAL_CHANGE_REASON : null,
                financial_change_idempotency_key: values.financial_change_idempotency_key || null,
                selected_at_change_reason: selectedAtChanged ? DEFAULT_SELECTED_AT_CHANGE_REASON : null,
                selected_at_change_idempotency_key: selectedAtRequestKey,
                service_type: 'CHOT',
                items: buildPayloadItems(),
            };

            if (isEditMode) {
                await dispatch(updateService(editServiceId, payload));
            } else {
                await dispatch(createService(payload));
            }

            if (typeof reloadData === 'function') {
                await reloadData(editServiceId);
            }
            onClose();
        } catch (e) {
            const errorMessage = e?.response?.data?.msg || '';
            const isAllocationConflict = e?.response?.status === 409
                && errorMessage.indexOf('Không thể tự cập nhật phân bổ cho role') === 0;
            if (isAllocationConflict) {
                Modal.warning({
                    title: 'Cần cập nhật phân bổ doanh thu',
                    content: errorMessage,
                });
            }
            setSaveLoading(false);
        }
    }, [
        form,
        items,
        boundCustomerId,
        buildPayloadItems,
        isEditMode,
        editServiceId,
        dispatch,
        reloadData,
        onClose,
        financialChanged,
        selectedAtChanged,
        isImplantService,
    ]);

    const selectedCount = items.length;

    return (
        <Drawer
            visible={visible}
            title={isEditMode ? 'Cập nhật dịch vụ chốt' : 'Thêm dịch vụ chốt'}
            onClose={onClose}
            maskClosable={false}
            width={isMobile ? '100%' : '68%'}
            bodyStyle={styles.drawerBody}
            footer={(
                <div style={{ textAlign: 'right' }}>
                    <Space>
                        <Button size="large" onClick={onClose}>
                            Huỷ
                        </Button>
                        <Button
                            loading={saveLoading}
                            type="primary"
                            onClick={handleSave}
                            size="large"
                            icon={<SaveOutlined />}
                        >
                            {isEditMode ? 'Cập nhật' : 'Lưu'}
                        </Button>
                    </Space>
                </div>
            )}
        >
            {visible ? (
                <div style={styles.pageWrap}>
                    <Form form={form} layout="vertical" className="customer-data-customer-service-form">
                        <div style={styles.topCard}>
                            <div style={styles.customerBox}>
                                <div style={styles.customerIcon}>
                                    <UserOutlined />
                                </div>

                                <div>
                                    <div style={styles.customerTitle}>Khách hàng</div>
                                    <div style={styles.customerName}>
                                        {currentCustomer
                                            ? `${currentCustomer?.full_name} - ${currentCustomer?.code}`
                                            : `Khách hàng #${boundCustomerId || '-'}`}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div style={styles.topCard}>
                            <div style={styles.sectionTitle}>Thông tin chốt dịch vụ</div>
                            <div style={styles.sectionDesc}>
                                Chọn dịch vụ, ngày chốt và ghi nhận nhân sự thực tế tham gia hoặc phụ trách dịch vụ.
                            </div>

                            <Row gutter={16}>
                                {!isEditMode && (
                                    <Col xs={24} md={14}>
                                        <Form.Item
                                            name="selected_service_id"
                                            label="Dịch vụ"
                                            style={styles.formItemTight}
                                        >
                                            <TreeSelect
                                                showSearch
                                                style={{ width: '100%' }}
                                                dropdownStyle={{ maxHeight: 450, overflow: 'auto' }}
                                                treeData={serviceTree}
                                                treeNodeFilterProp="title"
                                                allowClear
                                                placeholder="Chọn dịch vụ để thêm vào danh sách"
                                                treeDefaultExpandAll
                                                onChange={onChangeService}
                                            />
                                        </Form.Item>
                                    </Col>
                                )}

                                <Col xs={24} md={isEditMode ? 24 : 10}>
                                    <Form.Item
                                        name="selected_at"
                                        label="Ngày chốt"
                                        rules={[{ required: true, message: 'Vui lòng điền!' }]}
                                        style={styles.formItemTight}
                                    >
                                        <DatePicker
                                            className="w-100"
                                            format="DD/MM/YYYY"
                                            onChange={setSelectedAtValue}
                                            disabledDate={(date) => date && date.isAfter(moment().endOf('day'))}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                            {/* Tạm ẩn yêu cầu nhập lý do; hệ thống tự gắn lý do mặc định vào payload.
                            {selectedAtChanged && (
                                <Alert
                                    type="warning"
                                    message="Thay đổi ngày chốt"
                                    description={(
                                        <>
                                            <div style={{ marginBottom: 12 }}>
                                                Việc thay đổi ngày có thể làm thay đổi kỳ thống kê và kết quả hoa hồng liên quan. Phiếu thu, số tiền đã thanh toán, công nợ và số dư của khách hàng không bị ảnh hưởng.
                                            </div>
                                            <Form.Item
                                                name="selected_at_change_reason"
                                                label="Lý do thay đổi ngày chốt"
                                                rules={[{ required: true, whitespace: true, message: 'Vui lòng nhập lý do thay đổi ngày chốt.' }]}
                                                style={{ marginBottom: 0 }}
                                            >
                                                <Input.TextArea rows={3} maxLength={1000} />
                                            </Form.Item>

                                            <Form.Item name="selected_at_change_idempotency_key" hidden><Input /></Form.Item>
                                        </>
                                    )}
                                    style={{ marginTop: 16 }}
                                />
                            )}
                            */}
                            <Form.Item name="selected_at_change_idempotency_key" hidden><Input /></Form.Item>

                            {!isEditMode && selectedCount > 0 && (
                                <div style={styles.helperCard}>
                                    <div style={styles.helperTitle}>
                                        {selectedCount} dịch vụ đang được chọn
                                    </div>
                                    <div style={styles.helperText}>
                                        Thiết lập bác sĩ, phụ tá và nhân viên sales thực tế cho từng dịch vụ bên dưới.
                                    </div>
                                </div>
                            )}

                            {items.length > 0 && (
                                <div style={styles.totalCard}>
                                    <div style={styles.totalLabel}>Tổng giá trị dịch vụ</div>
                                    <div style={styles.totalValue}>
                                        {Number(form.getFieldValue('total_amount') || 0).toLocaleString('vi-VN')} đ
                                    </div>

                                    <Form.Item
                                        hidden
                                        name="total_amount"
                                        rules={[{ required: true, message: 'Vui lòng điền!' }]}
                                    >
                                        <InputNumber disabled />
                                    </Form.Item>
                                </div>
                            )}
                        </div>

                        <Spin tip="Loading..." spinning={isLoading}>
                            {items.length > 0 && (
                                <div style={styles.actionBar}>
                                    {!isEditMode ? (
                                        <>
                                            <Checkbox
                                                checked={selectAll}
                                                onChange={handleSelectAll}
                                            >
                                                Chọn tất cả
                                            </Checkbox>

                                            <Button
                                                disabled={!selectedItems.length}
                                                onClick={openBulkUpdate}
                                                type="primary"
                                                icon={<FormOutlined />}
                                            >
                                                Cập nhật đồng loạt
                                            </Button>
                                        </>
                                    ) : (
                                        <div style={{ fontWeight: 600, color: '#4b5563' }}>
                                            Danh sách dịch vụ đang chỉnh sửa
                                        </div>
                                    )}
                                </div>
                            )}

                            <div style={styles.collapseWrap}>
                                <Collapse
                                    expandIconPosition="end"
                                    ghost
                                    activeKey={activeCollapseKeys}
                                    onChange={(keys) => setActiveCollapseKeys(Array.isArray(keys) ? keys : [keys])}
                                >
                                    {items.length > 0 ? items.map((item, index) => {
                                        const isSelected = selectedItems.includes(item.id);

                                        return (
                                            <Panel
                                                key={item.id}
                                                style={(() => {
                                                    return {
                                                        borderRadius: 14,
                                                        marginBottom: 20,
                                                        overflow: 'hidden',
                                                        background: '#efefef'
                                                    };
                                                })()}
                                                header={(() => {
                                                    const serviceGroupLabel = item?.service_group_name || 'Chưa cấu hình';

                                                    return (
                                                        <div
                                                            style={{
                                                                ...styles.panelHeader,
                                                                borderRadius: 10,
                                                                padding: '8px 12px',
                                                            }}
                                                        >
                                                            <div style={styles.panelHeaderLeft}>
                                                                {!isEditMode && (
                                                                    <Checkbox
                                                                        checked={isSelected}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        onChange={(e) => handleItemSelect(item.id, e.target.checked)}
                                                                    />
                                                                )}

                                                                <div style={styles.panelIndex}>
                                                                    {index + 1}
                                                                </div>
                                                                <div style={styles.panelTitleWrap}>
                                                                    <div
                                                                        style={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: 8,
                                                                            flexWrap: 'wrap',
                                                                        }}
                                                                    >
                                                                        <div style={styles.panelTitle}>
                                                                            {item.name}
                                                                        </div>

                                                                        <span
                                                                            style={{
                                                                                fontSize: 11,
                                                                                fontWeight: 700,
                                                                                padding: '5px 12px',
                                                                                borderRadius: 999,
                                                                                background: '#edf4ff',
                                                                                color: '#3f74f7',
                                                                                border: '1px solid #d8e5ff',
                                                                                whiteSpace: 'nowrap',
                                                                            }}
                                                                        >
                                                                            {serviceGroupLabel}
                                                                        </span>
                                                                    </div>

                                                                    <div style={styles.panelSubTitle}>
                                                                        Thiết lập thông tin dịch vụ và nhân sự thực tế phụ trách
                                                                    </div>
                                                                </div>
                                                            </div>

                                                        </div>
                                                    );
                                                })()}
                                            >
                                                <div style={styles.panelBody}>
                                                    <div style={styles.blockCard}>
                                                        <div style={styles.blockTitle}>Thông tin dịch vụ</div>

                                                        <Row gutter={[16, 0]}>
                                                            <Col xs={24} md={8}>
                                                                <Form.Item label="Số lượng" style={styles.formItemTight}>
                                                                    <InputNumber
                                                                        value={item.quantity}
                                                                        min={1}
                                                                        className="w-100"
                                                                        formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                                        onChange={(v) => {
                                                                            const valid = v == null || v <= 0 ? 1 : v;
                                                                            onChangeItem('quantity', valid, item.id);
                                                                        }}
                                                                    />
                                                                </Form.Item>
                                                            </Col>

                                                            <Col xs={24} md={8}>
                                                                <Form.Item label="Giá bán" style={styles.formItemTight}>
                                                                    <InputNumber
                                                                        value={item.sale_price}
                                                                        min={0}
                                                                        precision={6}
                                                                        className="w-100"
                                                                        formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                                        onChange={(v) => onChangeItem('sale_price', v, item.id)}
                                                                    />
                                                                </Form.Item>
                                                            </Col>

                                                            <Col xs={24} md={8}>
                                                                <Form.Item label="Thành tiền" style={styles.formItemTight}>
                                                                    <InputNumber
                                                                        value={item.amount}
                                                                        min={0}
                                                                        precision={0}
                                                                        className="w-100"
                                                                        formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                                        onChange={(v) => onChangeItem('amount', v, item.id)}
                                                                    />
                                                                </Form.Item>
                                                            </Col>
                                                            {/* Tạm ẩn yêu cầu nhập lý do; hệ thống tự gắn lý do mặc định vào payload.
                                                            <Col xs={24}>
                                                                {financialChanged && (
                                                                    <Alert
                                                                        type="warning"
                                                                        message="Thay đổi giá trị dịch vụ"
                                                                        description={(
                                                                            <>
                                                                                <div style={{ marginBottom: 12 }}>
                                                                                    Việc thay đổi có thể làm thay đổi công nợ, số dư khách hàng, điều kiện áp dụng và hoa hồng liên quan. Nếu số tiền đã thanh toán vượt quá số tiền cần thanh toán sau khi điều chỉnh, phần chênh lệch sẽ được chuyển vào số dư của khách hàng.
                                                                                </div>
                                                                                <Form.Item
                                                                                    name="financial_change_reason"
                                                                                    label="Lý do thay đổi"
                                                                                    rules={[{ required: true, whitespace: true, message: 'Vui lòng nhập lý do thay đổi.' }]}
                                                                                    style={{ marginBottom: 0 }}
                                                                                >
                                                                                    <Input.TextArea rows={3} maxLength={1000} />
                                                                                </Form.Item>

                                                                                <Form.Item name="financial_change_idempotency_key" hidden><Input /></Form.Item>
                                                                            </>
                                                                        )}
                                                                        style={{ marginTop: 16 }}
                                                                    />
                                                                )}
                                                            </Col>
                                                             */}
                                                            <Form.Item name="financial_change_idempotency_key" hidden><Input /></Form.Item>

                                                            <Col xs={24}>
                                                                <Form.Item label="Trạng thái" style={styles.formItemTight}>
                                                                    <Radio.Group
                                                                        onChange={(e) => onChangeItem('status', e.target.value, item.id)}
                                                                        value={item.status}
                                                                    >
                                                                        {customerServiceStatus?.map((opt) => (
                                                                            <Radio key={opt.value} value={opt.value}>
                                                                                {opt.label}
                                                                            </Radio>
                                                                        ))}
                                                                    </Radio.Group>
                                                                </Form.Item>
                                                            </Col>
                                                        </Row>
                                                    </div>

                                                    <div style={styles.blockCard}>
                                                        <div style={styles.blockTitle}>Nhân sự phụ trách</div>

                                                        <Row gutter={[16, 0]}>
                                                            <Col xs={24} md={12}>
                                                                <Form.Item label="Nhân viên sales" style={styles.formItemTight}>
                                                                    <BaseSelect
                                                                        options={sales ?? []}
                                                                        optionValue="user_id"
                                                                        optionLabel="display_name"
                                                                        mode="multiple"
                                                                        showSearch
                                                                        style={{ width: '100%' }}
                                                                        value={item.sales || []}
                                                                        disabled={item.sales_unknown === true}
                                                                        placeholder="Chọn 1 hoặc nhiều nhân viên sales"
                                                                        onChange={(vals) => onChangeItem('sales', vals, item.id)}
                                                                    />
                                                                    <Checkbox
                                                                        checked={item.sales_unknown === true}
                                                                        onChange={(event) => onChangeItem('sales_unknown', event.target.checked, item.id)}
                                                                        style={{ marginTop: 6 }}
                                                                    >
                                                                        Chưa xác định
                                                                    </Checkbox>
                                                                </Form.Item>
                                                            </Col>

                                                            <Col xs={24} md={12}>
                                                                <Form.Item label="Phụ tá" style={styles.formItemTight}>
                                                                    <BaseSelect
                                                                        options={assistants ?? []}
                                                                        optionValue="user_id"
                                                                        optionLabel="display_name"
                                                                        mode="multiple"
                                                                        placeholder="Chọn 1 hoặc nhiều phụ tá"
                                                                        showSearch
                                                                        style={{ width: '100%' }}
                                                                        value={item.assistants || []}
                                                                        onChange={(vals) => onChangeItem('assistants', vals, item.id)}
                                                                    />
                                                                </Form.Item>
                                                            </Col>

                                                            <CustomerServiceIndependentStaffFields
                                                                item={item}
                                                                doctors={doctors ?? []}
                                                                isImplant={isImplantService(item)}
                                                                onChangeItem={onChangeItem}
                                                                formItemStyle={styles.formItemTight}
                                                            />
                                                        </Row>
                                                    </div>
                                                    <div style={styles.blockCard}>
                                                        <div style={styles.blockTitle}>Ghi chú cho dịch vụ</div>

                                                        <Form.Item style={{ marginBottom: 0 }}>
                                                            <Input.TextArea
                                                                value={item.note}
                                                                rows={3}
                                                                onChange={(e) => onChangeItem('note', e.target.value, item.id)}
                                                                style={styles.noteArea}
                                                                placeholder="Nhập ghi chú thêm cho dịch vụ này"
                                                            />
                                                        </Form.Item>
                                                    </div>
                                                    {!isEditMode && (
                                                        <div style={styles.deleteWrap}>
                                                            <Button
                                                                onClick={() => onDelete(item.id)}
                                                                danger
                                                                icon={<DeleteOutlined />}
                                                            >
                                                                Xóa dịch vụ
                                                            </Button>
                                                        </div>
                                                    )}
                                                </div>
                                            </Panel>
                                        );
                                    }) : (
                                        <div style={{ textAlign: 'center' }}>
                                            <Empty
                                                imageStyle={{ height: 30 }}
                                                style={{ margin: '10px 0' }}
                                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                            />
                                        </div>
                                    )}
                                </Collapse>
                            </div>
                        </Spin>

                        <Modal
                            title="Cập nhật đồng loạt nhân sự"
                            open={bulkVisible}
                            onCancel={closeBulkUpdate}
                            onOk={applyBulkUpdate}
                            okText="Áp dụng"
                            cancelText="Huỷ"
                            maskClosable={false}
                            width={isMobile ? '100%' : '50%'}
                        >
                            <div style={styles.modalCard}>
                                <Form layout="vertical">
                                    <Form.Item label="Chế độ cập nhật">
                                        <Radio.Group
                                            value={bulkData.mode}
                                            onChange={(e) => setBulkData((prev) => ({ ...prev, mode: e.target.value }))}
                                        >
                                            <Radio value="update">Cập nhật</Radio>
                                            <Radio value="clear">Xóa toàn bộ</Radio>
                                        </Radio.Group>
                                    </Form.Item>

                                    <Form.Item label="Nhân viên sales">
                                        <BaseSelect
                                            options={sales ?? []}
                                            optionValue="user_id"
                                            optionLabel="display_name"
                                            mode="multiple"
                                            showSearch
                                            style={{ width: '100%' }}
                                            placeholder="Chọn nhân viên sales"
                                            value={bulkData.sales}
                                            onChange={(vals) => setBulkField('sales', vals)}
                                            disabled={bulkData.mode === 'clear'}
                                        />
                                    </Form.Item>

                                    {[
                                        ['examination_doctor_ids', 'Bác sĩ khám'],
                                        ['treatment_doctor_ids', 'Bác sĩ điều trị'],
                                        ['implant_doctor_ids', 'Bác sĩ cắm trụ'],
                                        ['prosthetic_doctor_ids', 'Bác sĩ phục hình'],
                                        ['apprentice_doctor_ids', 'Bác sĩ học việc'],
                                    ].map(([field, label]) => (
                                        <Form.Item label={label} key={field}>
                                            <BaseSelect
                                                options={doctors ?? []}
                                                optionValue="user_id"
                                                optionLabel="display_name"
                                                mode="multiple"
                                                showSearch
                                                style={{ width: '100%' }}
                                                placeholder={`Chọn ${label.toLowerCase()}`}
                                                value={bulkData[field]}
                                                onChange={(vals) => setBulkField(field, vals)}
                                                disabled={bulkData.mode === 'clear'}
                                            />
                                        </Form.Item>
                                    ))}

                                    <Form.Item label="Phụ tá" style={{ marginBottom: 0 }}>
                                        <BaseSelect
                                            options={assistants ?? []}
                                            optionValue="user_id"
                                            optionLabel="display_name"
                                            mode="multiple"
                                            showSearch
                                            style={{ width: '100%' }}
                                            placeholder="Chọn phụ tá"
                                            value={bulkData.assistants}
                                            onChange={(vals) => setBulkField('assistants', vals)}
                                            disabled={bulkData.mode === 'clear'}
                                        />
                                    </Form.Item>
                                </Form>
                            </div>
                        </Modal>
                    </Form>
                </div>
            ) : null}
        </Drawer>
    );
}

export default CustomerServiceForm;
